import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import DynamicTable from "../../components/DynamicTable";
import NoteModal from "../../components/NoteModal";
import { useTenant } from "../../context/TenantContext";
import {
  fetchGeneratedNoteDocument,
  getNotesAddedYesNo,
  notesAddedDisplay,
} from "../../services/generatedNotePresence";
import {
  formatEvaluationFormTypeDisplay,
  inferEvaluationFormCompletedBy,
} from "./evaluationFormRole";
import { listDateFilterPayload } from "./listDateFilters";
import {
  EVALUATION_LIST_STATE_FILTER_OPTIONS,
  evaluationStateColumnLabel,
} from "./mindtalkConsultationState";
import styles from "./styles.module.css";

const PER_PAGE = 10;
const NOTE_KIND = "evaluation_form";

function odooRelationLabel(rel) {
  if (rel == null || rel === false) return "";
  if (Array.isArray(rel) && rel[1] != null) {
    const s = String(rel[1]);
    return s.split("[")[0]?.trim() || s;
  }
  return "";
}

const emptyFilters = () => ({
  date: "all",
  selectedDate: null,
  /** Used when `date === "custom_range"` (YYYY-MM-DD inclusive bounds). */
  dateFrom: null,
  dateTo: null,
  state: "all",
  name: "",
  doctorId: null,
  /** `followup.type` master record id — populated via patientService.fetchFollowupTypes() */
  followupTypeId: null,
  evaluationFormId: "",
});

function stateChipColor(label) {
  const s = String(label || "").toLowerCase();
  if (s === "approved") return "success";
  if (s === "confirmed") return "info";
  if (s === "draft") return "default";
  return "default";
}

function StateChip({ label }) {
  if (!label) return <span style={{ color: "#9ca3af" }}>—</span>;
  return <Chip size="small" label={label} color={stateChipColor(label)} variant="filled" />;
}

function NotesChip({ value }) {
  if (value === "Yes")
    return <Chip size="small" label="Yes" color="success" sx={{ minWidth: 48 }} />;
  return (
    <Chip
      size="small"
      label="No"
      variant="outlined"
      sx={{ minWidth: 48, color: "text.secondary" }}
    />
  );
}

/** See ConsultationListSection.pickConsultationDatetime for the rationale. */
function pickEvaluationDatetime(row) {
  const candidates = [
    row.evaluation_date,
    row.consultation_date,
    row.appointment_date,
    row.scheduled_date,
    row.start_date,
    row.start_datetime,
    row.create_date,
    row.date,
  ];
  for (const v of candidates) {
    if (v != null && v !== false && String(v).trim() !== "") return v;
  }
  return null;
}

function formatDisplayDate(raw, tz) {
  if (raw == null || raw === "") return "";
  const s = String(raw);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      timeZone: tz,
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  }
  const utcish = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}$/.test(s)
    ? s.replace(" ", "T") + "Z"
    : s;
  const d = new Date(utcish);
  if (Number.isNaN(d.getTime())) return s;
  const opts = tz ? { timeZone: tz } : {};
  return d.toLocaleString(undefined, opts);
}

function formatEvaluationFormRow(row, tz) {
  const patient =
    row.patient_id?.[1]?.split("[")[0]?.trim() || row.patient || "";
  const idDisplay =
    row.display_name != null
      ? String(row.display_name)
      : row.name != null
        ? String(row.name)
        : String(row.id ?? "");
  const dateRaw = pickEvaluationDatetime(row);
  const dateStr = formatDisplayDate(dateRaw, tz);
  const followup =
    Array.isArray(row.followup_type_id) && row.followup_type_id[1]
      ? String(row.followup_type_id[1])
      : "";
  let opVisit = "";
  if (row.op_visit_id != null && row.op_visit_id !== false) {
    opVisit = Array.isArray(row.op_visit_id)
      ? String(row.op_visit_id[1] ?? row.op_visit_id[0] ?? "")
      : String(row.op_visit_id);
  }
  const backendState = row.state ?? "";
  const doctorName = odooRelationLabel(row.psychiatrist_id);
  return {
    ...row,
    name: idDisplay,
    patient,
    doctor_name: doctorName,
    date: dateStr,
    followup_type: followup,
    op_visit: opVisit,
    state: evaluationStateColumnLabel(backendState),
  };
}

export default function EvaluationFormListSection({
  patientService,
  doctorOptions,
  onSelectEvaluationRow,
  psychiatrists = [],
  psychologists = [],
}) {
  const [followupTypeOptions, setFollowupTypeOptions] = useState(() => [
    { value: null, label: "All follow-up types" },
  ]);
  const { db, tenant } = useTenant();
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const [nameInput, setNameInput] = useState("");
  const [debouncedName, setDebouncedName] = useState("");

  const [viewNoteModalOpen, setViewNoteModalOpen] = useState(false);
  const [viewNotePayload, setViewNotePayload] = useState(null);

  const useMindtalkFullDoctorUi =
    tenant?.features?.hrEmployeeDoctorLists === "all_roles";
  const displayTimeZone = tenant?.features?.displayTimeZone;

  const formatTimestamp = useCallback(
    (timestamp) => {
      if (timestamp == null) return "N/A";
      if (typeof timestamp === "string") {
        const d = new Date(timestamp);
        if (Number.isNaN(d.getTime())) return timestamp;
        const opts = displayTimeZone ? { timeZone: displayTimeZone } : {};
        return d.toLocaleString(undefined, opts);
      }
      if (timestamp instanceof Date) {
        const opts = displayTimeZone ? { timeZone: displayTimeZone } : {};
        return timestamp.toLocaleString(undefined, opts);
      }
      return "N/A";
    },
    [displayTimeZone],
  );

  const formatType = useCallback(
    (type) => {
      if (!type) return "N/A";
      if (!useMindtalkFullDoctorUi) {
        const typeMap = {
          psychiatric: "Psychiatric",
          psychologist: "Psychologist",
          evaluationform: "Evaluation Form",
        };
        return typeMap[type] || type;
      }
      const typeMap = {
        psychiatric: "Psychiatric",
        psychologist: "Psychologist",
        special_educator: "Special Educator",
        occupational_therapist: "Occupational Therapist",
        speech_therapist: "Speech Therapist",
        rehabilitation_psychologist: "Rehabilitation Psychologist",
        pediatric_physiotherapist: "Pediatric Physiotherapist",
        evaluationform: "Evaluation Form",
      };
      return typeMap[type] || type;
    },
    [useMindtalkFullDoctorUi],
  );

  const buildNoteForModal = useCallback(
    (raw, recordId) => {
      const rawType = raw.type;
      const typeDisplay =
        rawType === "evaluationform"
          ? formatEvaluationFormTypeDisplay(raw, psychiatrists, psychologists)
          : formatType(rawType);
      const evaluationFormCompletedBy =
        rawType === "evaluationform"
          ? inferEvaluationFormCompletedBy(raw, psychiatrists, psychologists)
          : undefined;
      const idDisplay =
        raw.id != null && raw.id !== ""
          ? String(raw.id)
          : String(recordId ?? "");
      const body =
        raw.note ??
        raw.generated_note ??
        raw.content ??
        (typeof raw.text === "string" ? raw.text : "");
      return {
        ...raw,
        typeRaw: rawType,
        type: typeDisplay,
        evaluationFormCompletedBy,
        id: idDisplay,
        created: formatTimestamp(raw.created),
        updated: formatTimestamp(raw.updated),
        note:
          typeof body === "string"
            ? body
            : body != null
              ? String(body)
              : "",
      };
    },
    [formatTimestamp, formatType, psychiatrists, psychologists],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await patientService.fetchFollowupTypes();
        /**
         * Whitelist of the 3 follow-up types relevant to evaluation forms. The Cadabams
         * `followup.type` master has near-duplicates per type (an "-IP VISIT" entry and a
         * "Consultation - OP" entry). The dropdown surfaces our own labels and points at
         * the OP variants for Psychiatrist/Psychologist and the canonical Single IP entry
         * for the third.
         */
        const FOLLOWUP_PATTERNS = [
          {
            label: "Psychiatrist",
            // e.g. "Psychiatrist Consultation - OP" (id 27)
            match: (n) =>
              /psychiatrist/i.test(n) &&
              !/psychologist/i.test(n) &&
              !/\bip\b/i.test(n) &&
              !/single/i.test(n),
          },
          {
            label: "Psychologist",
            // e.g. "Psychologist Consultation - OP" (id 28) — exclude "-IP" variants
            // such as "PSYCHOLOGIST-IP VISIT" (id 2)
            match: (n) =>
              /psychologist/i.test(n) &&
              !/\bip\b/i.test(n) &&
              !/counselling/i.test(n) &&
              !/family/i.test(n),
          },
          {
            label: "Single Psychiatrist IP visit",
            // e.g. "SINGLE PSYCHIATRIST-IP VISIT" (id 1) or "SINGLE-PSYCHIATRIST-IP VISIT" (id 25)
            match: (n) =>
              /single/i.test(n) && /psychiatrist/i.test(n) && /\bip\b/i.test(n),
          },
        ];
        const fetched = list || [];
        const matched = FOLLOWUP_PATTERNS.map(({ label, match }) => {
          const found = fetched.find((t) => match(String(t.name || "")));
          return found ? { value: String(found.id), label } : null;
        }).filter(Boolean);
        const opts = [{ value: null, label: "All follow-up types" }, ...matched];
        if (!cancelled) setFollowupTypeOptions(opts);
      } catch (e) {
        console.warn("[EvaluationFormListSection] followup-types", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientService, tenant?.id]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedName(nameInput.trim()), 300);
    return () => clearTimeout(t);
  }, [nameInput]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, name: debouncedName }));
    setCurrentPage(1);
  }, [debouncedName]);

  const listFilters = useMemo(() => {
    const datePart = listDateFilterPayload(filters);
    // Evaluation states (draft / confirm / approved) are sent as-is; the consultation-only
    // `ended` → `Check_Out` remap never applied to this model.
    return {
      state: filters.state,
      name: filters.name || undefined,
      ...datePart,
      doctorId:
        filters.doctorId != null && filters.doctorId !== ""
          ? filters.doctorId
          : undefined,
      followupTypeId:
        filters.followupTypeId != null && filters.followupTypeId !== ""
          ? filters.followupTypeId
          : undefined,
      evaluationFormId: filters.evaluationFormId?.trim() || undefined,
    };
  }, [filters]);

  const filtersActive = Boolean(
    (filters.date && filters.date !== "all") ||
      (filters.state && filters.state !== "all") ||
      filters.name ||
      filters.doctorId ||
      filters.followupTypeId ||
      filters.evaluationFormId,
  );

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The server's total_records ignores `domain`, so count the matching ids ourselves
      // for a filtered view. Fired alongside the page request.
      const [{ data, total }, exactCount] = await Promise.all([
        patientService.fetchPsychiatristEvaluationFormsList(
          currentPage,
          PER_PAGE,
          listFilters,
        ),
        filtersActive
          ? patientService.countPsychiatristEvaluationForms(listFilters)
          : Promise.resolve(null),
      ]);
      const sorted = [...(data || [])].sort((a, b) => {
        const da = a.date ? new Date(a.date) : new Date(0);
        const dbb = b.date ? new Date(b.date) : new Date(0);
        return dbb - da;
      });
      const baseRows = sorted.map((row) => formatEvaluationFormRow(row, displayTimeZone));
      const notesFlags = await Promise.all(
        sorted.map((row) => getNotesAddedYesNo(db, row.id, NOTE_KIND, row.patient_id?.[0])),
      );
      setRows(
        baseRows.map((r, i) => {
          const yesNo = notesAddedDisplay(notesFlags[i]);
          return {
            ...r,
            notesAdded: yesNo,
            notesAddedCell: <NotesChip value={yesNo} />,
            stateCell: <StateChip label={r.state} />,
          };
        }),
      );
      if (filtersActive) {
        // `null` = probe hit its cap; fall back to "at least N" paging.
        setTotalRecords(exactCount);
      } else {
        setTotalRecords(typeof total === "number" ? total : sorted.length);
      }
    } catch (e) {
      console.error("[EvaluationFormListSection]", e);
      setError(e?.message || "Failed to load evaluation forms");
      setRows([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [patientService, currentPage, listFilters, filtersActive, db, displayTimeZone]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  /**
   * `total_records` ignores `domain`, so a filtered view gets its total from the count probe
   * in fetchList. See ConsultationListSection for the reasoning.
   */
  const totalPages =
    totalRecords == null
      ? null
      : !totalRecords
        ? 1
        : Math.max(1, Math.ceil(totalRecords / PER_PAGE) || 1);

  /** Only needed when the exact total is unavailable (count probe hit its cap). */
  const hasNextPage = totalPages == null ? rows.length >= PER_PAGE : undefined;

  const evaluationFormIdInvalid = (() => {
    const raw = String(filters.evaluationFormId ?? "").trim();
    return raw !== "" && !Number.isFinite(parseInt(raw, 10));
  })();

  const headers = [
    { key: "name", label: "Display name" },
    { key: "patient", label: "Patient" },
    { key: "doctor_name", label: "Psychiatrist" },
    { key: "date", label: "Date" },
    { key: "followup_type", label: "Follow-up type" },
    { key: "op_visit", label: "OP visit" },
    { key: "stateCell", label: "State" },
    { key: "notesAddedCell", label: "Notes Added" },
  ];

  const handleRowClick = async (row) => {
    if (typeof onSelectEvaluationRow !== "function") return;
    const formId = row.id;
    if (formId == null) return;

    let patientId = row.patient_id?.[0] ?? null;
    let doctorIdForNotes = null;

    try {
      const detail = await patientService.fetchPsychiatristEvaluationFormById(formId);
      if (detail?.patient_id?.[0] != null) patientId = detail.patient_id[0];
      const dr = detail?.psychiatrist_id;
      if (dr != null) {
        doctorIdForNotes = Array.isArray(dr) ? String(dr[0]) : String(dr);
      }
    } catch (e) {
      console.warn("[EvaluationFormListSection] detail fetch fallback", e);
      if (row.psychiatrist_id) {
        const dr = row.psychiatrist_id;
        doctorIdForNotes = Array.isArray(dr) ? String(dr[0]) : String(dr);
      }
    }

    onSelectEvaluationRow({
      consultationId: formId,
      patientId,
      noteType: "evaluationform",
      doctorIdForNotes,
    });
  };

  const closeViewNoteModal = useCallback(() => {
    setViewNoteModalOpen(false);
    setViewNotePayload(null);
  }, []);

  const handleViewGeneratedNote = useCallback(
    async (row) => {
      const recordId = row.id;
      if (recordId == null) return;

      setViewNoteModalOpen(true);
      setViewNotePayload({
        id: String(recordId),
        type: "…",
        typeRaw: undefined,
        note: "Loading…",
        created: "—",
        updated: "—",
      });

      try {
        const raw = await fetchGeneratedNoteDocument(
          db,
          recordId,
          NOTE_KIND,
          row.patient_id?.[0],
        );
        if (!raw) {
          setViewNotePayload({
            id: String(recordId),
            type: "N/A",
            typeRaw: undefined,
            note: "No generated note document was found for this evaluation form.",
            created: "N/A",
            updated: "N/A",
          });
          return;
        }
        setViewNotePayload(buildNoteForModal(raw, recordId));
      } catch (e) {
        console.warn("[EvaluationFormListSection] view note fetch", e);
        setViewNotePayload({
          id: String(recordId),
          type: "Error",
          typeRaw: undefined,
          note: e?.message || "Failed to load the generated note.",
          created: "N/A",
          updated: "N/A",
        });
      }
    },
    [buildNoteForModal, db],
  );

  const tableActions = useMemo(
    () => [
      {
        label: "View note",
        showIf: (row) => row.notesAdded === "Yes",
        onClick: handleViewGeneratedNote,
      },
    ],
    [handleViewGeneratedNote],
  );

  const clearFilters = () => {
    setFilters(emptyFilters());
    setNameInput("");
    setCurrentPage(1);
  };

  return (
    <Box sx={{ mb: 3 }}>
      {error && (
        <div className={styles.errorBanner} style={{ marginBottom: 16 }}>
          <p>{error}</p>
          <button type="button" onClick={() => fetchList()}>
            Retry
          </button>
        </div>
      )}

      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <FilterAltOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          <Typography variant="subtitle2" color="text.secondary">
            Filters
          </Typography>
        </Stack>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "flex-end",
          }}
        >
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="eval-date">Date</InputLabel>
          <Select
            labelId="eval-date"
            label="Date"
            value={filters.date}
            onChange={(e) => {
              const v = e.target.value;
              setFilters((prev) => ({
                ...prev,
                date: v,
                selectedDate: v === "custom" ? prev.selectedDate : null,
                dateFrom: v === "custom_range" ? prev.dateFrom : null,
                dateTo: v === "custom_range" ? prev.dateTo : null,
              }));
              setCurrentPage(1);
            }}
          >
            <MenuItem value="all">All dates</MenuItem>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="this_week">This week</MenuItem>
            <MenuItem value="this_month">This month</MenuItem>
            <MenuItem value="custom">Custom date</MenuItem>
            <MenuItem value="custom_range">Custom range</MenuItem>
          </Select>
        </FormControl>

        {filters.date === "custom" && (
          <TextField
            size="small"
            label="Day"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.selectedDate || ""}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, selectedDate: e.target.value }));
              setCurrentPage(1);
            }}
          />
        )}

        {filters.date === "custom_range" && (
          <>
            <TextField
              size="small"
              label="From"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.dateFrom || ""}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, dateFrom: e.target.value }));
                setCurrentPage(1);
              }}
              inputProps={filters.dateTo ? { max: filters.dateTo } : undefined}
            />
            <TextField
              size="small"
              label="To"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.dateTo || ""}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, dateTo: e.target.value }));
                setCurrentPage(1);
              }}
              inputProps={filters.dateFrom ? { min: filters.dateFrom } : undefined}
            />
          </>
        )}

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="eval-state">State</InputLabel>
          <Select
            labelId="eval-state"
            label="State"
            value={filters.state}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, state: e.target.value }));
              setCurrentPage(1);
            }}
          >
            {EVALUATION_LIST_STATE_FILTER_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/*
          The domain builder does `parseInt` and drops the predicate when the result is NaN,
          so free text used to return the full unfiltered list with no feedback. Flag it
          instead of silently ignoring it.
        */}
        <TextField
          size="small"
          label="Evaluation form ID"
          value={filters.evaluationFormId}
          onChange={(e) => {
            setFilters((prev) => ({ ...prev, evaluationFormId: e.target.value }));
            setCurrentPage(1);
          }}
          placeholder="Exact ID"
          sx={{ minWidth: 160 }}
          inputProps={{ inputMode: "numeric" }}
          error={evaluationFormIdInvalid}
          helperText={evaluationFormIdInvalid ? "Numbers only — filter not applied" : " "}
        />

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="eval-doc">Psychiatrist</InputLabel>
          <Select
            labelId="eval-doc"
            label="Psychiatrist"
            value={
              filters.doctorId != null && filters.doctorId !== ""
                ? String(filters.doctorId)
                : ""
            }
            onChange={(e) => {
              const v = e.target.value;
              setFilters((prev) => ({ ...prev, doctorId: v === "" ? null : v }));
              setCurrentPage(1);
            }}
          >
            {doctorOptions.map((opt) => (
              <MenuItem
                key={opt.value === null ? "all" : String(opt.value)}
                value={opt.value === null ? "" : String(opt.value)}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="eval-followup">Follow-up type</InputLabel>
          <Select
            labelId="eval-followup"
            label="Follow-up type"
            value={
              filters.followupTypeId != null && filters.followupTypeId !== ""
                ? String(filters.followupTypeId)
                : ""
            }
            onChange={(e) => {
              const v = e.target.value;
              setFilters((prev) => ({
                ...prev,
                followupTypeId: v === "" ? null : v,
              }));
              setCurrentPage(1);
            }}
          >
            {followupTypeOptions.map((opt) => (
              <MenuItem
                key={opt.value === null ? "all-followup" : String(opt.value)}
                value={opt.value === null ? "" : String(opt.value)}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Search patient name"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          sx={{ minWidth: 220 }}
        />

        <Button variant="outlined" size="small" onClick={clearFilters}>
          Clear filters
        </Button>
        </Box>
      </Paper>

      <DynamicTable
        title="Psychiatrist evaluation forms"
        headers={headers}
        data={rows}
        actions={tableActions}
        onRowClick={handleRowClick}
        currentPage={currentPage}
        totalPages={totalPages}
        hasNextPage={hasNextPage}
        emptyMessage={
          currentPage > 1
            ? "No more data available for the current filters."
            : undefined
        }
        onPageChange={(p) => setCurrentPage(p)}
        isLoading={loading}
      />

      {viewNotePayload != null && (
        <NoteModal
          isOpen={viewNoteModalOpen}
          onClose={closeViewNoteModal}
          note={viewNotePayload}
        />
      )}
    </Box>
  );
}
